import * as React from "react";
import { homeLink, request } from "../../../../../services/cpln";
import { Button, Card, Space, Tooltip } from "antd";
import { CardTitle } from "../../../../../components/antd/card/CardTitle";
import { CardContent } from "../../../../../components/antd/card/CardContent";
import { CardCollapsibleContent } from "../../../../../components/antd/card/CardCollapsibleContent";
import { CaretRightOutlined, EllipsisOutlined } from "@ant-design/icons";
import { v4 as uuidv4 } from "uuid";
import { useAppSelector } from "../../../../../store/hooks";
import { selectOrg } from "../../../../../store/contextSlice";
import { NGProviderBrowser } from "../../../ngProviderBrowser";
import { inputToAwsProvider, inputToMk8s } from "../../../draft/utils/inputToMk8s";
import { ProviderPreInstallScript } from "../../../advanced/providerPreInstallScript";
import { ProviderAutoscaler } from "../../../advanced/providerAutoscaler";
import { ProviderNetworking } from "../../../advanced/providerNetworking";
import { AWSInstructions, ProviderAWSInstructions } from "../../../provider_aws_instructions";
import { Mk8sCluster } from "../../../../../schema/types/mk8s/entity/mk8s";
import { dateString } from "../../../../../components/dateStringFn";
import { formatStrings } from "../../../../../utils/time";
import { AwsPolicyIcon } from "../../../awsPolicyIcon";
import { useDrawerContext } from "../../../../../components/antd/drawerContext";
import { getDefaultAwsInput } from "../../../draft/utils/utils";
import { FormItemValue } from "../../../../../components/antd/form/FormItemValue";
import { AwsSsmIcon } from "../../../awsSsmIcon";
import { FormItemDescription } from "../../../../../components/antd/form/FormItemDescription";
import { ExternalLink } from "../../../../../newcomponents/table/components/ExternalLink";
import { formSetValue, formTriggerValidation, useCpFormValue } from "../../../../gvc/testNewForm/utils";
import { CpInput } from "../../../../gvc/testNewForm/CpInput";
import { CpDescription } from "../../../../gvc/testNewForm/CpDescription";
import { CpFormItem } from "../../../../gvc/testNewForm/CpFormItem";
import { CpLabel } from "../../../../gvc/testNewForm/CpLabel";
import { CpErrorWarning } from "../../../../gvc/testNewForm/CpErrorWarning";
import { CpRadioGroup } from "../../../../gvc/testNewForm/CpRadioGroup";
import { CpSelect } from "../../../../gvc/testNewForm/CpSelect";
import { CpArrayString } from "../../../../gvc/testNewForm/CpArrayString";
import { CpArrayObject } from "../../../../gvc/testNewForm/CpArrayObject";
import { CpArrayItemNameValuePair } from "../../../../workload/draft/CpArrayItemNameValuePair";
import { CpArrayItemDeployRoleChain } from "./CpArrayItemDeployRoleChain";
import { CpSwitch } from "../../../../gvc/testNewForm/CpSwitch";
import { store } from "../../../../../store";
import { Mk8sInput } from "../../../../../schema/types/mk8s/input/mk8s";
import { useCpFormContext } from "../../../../../components/form/useCpFormContext";
import { MoreHorizontal } from "react-feather";
import { CpErrorWarningArrayIcon } from "../../../../gvc/testNewForm/CpErrorWarningArrayIcon";
import { CpDirtyIndicatorArrayIcon } from "../../../../gvc/testNewForm/CpDirtyIndicatorArrayIconLink";
import { toSelectOptions } from "../../../../../services/defaults";
import { DefaultOptionType } from "antd/es/select";

interface Props {}
export const AwsProviderSelf: React.FC<Props> = ({}) => {
  const { id: formId } = useCpFormContext();
  const org = useAppSelector(selectOrg);

  const drawer = useDrawerContext();

  const [browserKey, setBrowserKey] = React.useState("");

  const [regionOptions, setRegionOptions] = React.useState<DefaultOptionType[]>([]);
  React.useEffect(() => {
    discoverRegionOptions();
  }, []);
  async function discoverRegionOptions() {
    try {
      const form = store.getState().form.instances[formId];
      const values: Mk8sInput = form.values as any;

      const { data } = await request({
        service: "api",
        url: homeLink("mk8s") + "/-discover",
        method: "post",
        body: {
          objectType: "regions",
          cluster: inputToMk8s(values),
        },
      });
      setRegionOptions(toSelectOptions(data.regions));
    } catch (e) {
      setRegionOptions([]);
    }
  }

  const [recommendedAmis, setRecommendedAmis] = React.useState<any[]>([]);

  const [awsDeployRoleArnInstructions, setAwsDeployRoleArnInstructions] = React.useState<AWSInstructions | null>(null);
  const [awsInstructionsError, setAwsInstructionsError] = React.useState<string>("");

  React.useEffect(() => {
    fetchRecommendedAmis();
    fetchAwsInstructions();
  }, []);

  async function fetchRecommendedAmis() {
    try {
      const cluster: Mk8sCluster = {
        name: "discover",
        spec: {
          provider: {
            aws: inputToAwsProvider(getDefaultAwsInput()),
          },
          version: "1.35.3",
        },
      } as any;
      const { data } = await request({
        service: "api",
        url: homeLink("mk8s") + "/-discover",
        method: "post",
        body: {
          objectType: "recommended-amis",
          cluster: cluster,
        },
      });
      const res = data.recommended.map((r: any) => ({ label: r.name, value: r.name }));
      setRecommendedAmis(res);
    } catch (e) {
      setRecommendedAmis([]);
    }
  }

  async function fetchAwsInstructions() {
    try {
      const { data } = await request({ url: `/org/${org}/mk8s/-instructions/aws` });
      setAwsDeployRoleArnInstructions(data);
    } catch (e) {
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      setAwsInstructionsError(errorMessage);
    }
  }

  const [isAdvancedCollapsed, setIsAdvancedCollapsed] = React.useState(true);

  const isCreate = useCpFormValue(`meta.isCreate`);
  const imageType = useCpFormValue(`aws.image.type`);
  const region = useCpFormValue(`aws.region`);
  const vpcId = useCpFormValue(`aws.vpcId`);
  const keyPair = useCpFormValue(`aws.keyPair`);
  const diskEncryptionKeyArn = useCpFormValue(`aws.diskEncryptionKeyArn`);
  const securityGroupIds = useCpFormValue(`aws.securityGroupIds`);
  const extraNodePolicies = useCpFormValue(`aws.extraNodePolicies`);

  return (
    <>
      <div className="cpln-form-section cpln-ant-card-container">
        <Card>
          <CardTitle>
            AWS Provider
            <CpErrorWarningArrayIcon
              names={[
                `aws.autoscaler`,
                `aws.awsTags`,
                `aws.deployRoleArn`,
                `aws.deployRoleChain`,
                `aws.diskEncryptionKeyArn`,
                `aws.extraNodePolicies`,
                `aws.image`,
                `aws.keyPair`,
                `aws.networking`,
                `aws.preInstallScript`,
                `aws.region`,
                `aws.securityGroupIds`,
                `aws.skipCreateRoles`,
                `aws.vpcId`,
              ]}
            />
          </CardTitle>
          <CardContent>
            <CpFormItem>
              <CpLabel name={`aws.deployRoleArn`}>Deploy Role ARN</CpLabel>
              <CpDescription>
                <div>
                  {awsInstructionsError || !awsDeployRoleArnInstructions ? null : (
                    <span
                      className="color-link cursor-pointer"
                      onClick={() => {
                        drawer.setTemporary(
                          `mk8s-aws-instructions`,
                          "How to get Deploy Role ARN?",
                          <>
                            <ProviderAWSInstructions instructions={awsDeployRoleArnInstructions} />
                          </>,
                          900,
                        );
                      }}
                    >
                      How to get Deploy Role ARN?
                    </span>
                  )}
                </div>
              </CpDescription>
              <CpInput name={`aws.deployRoleArn`} style={{ width: 600 }} />
              <CpErrorWarning name={`aws.deployRoleArn`} />
            </CpFormItem>
            {isCreate ? (
              <CpFormItem>
                <CpLabel name={`aws.region`}>Region</CpLabel>
                {regionOptions.length > 0 ? (
                  <CpSelect name={`aws.region`} options={regionOptions} style={{ maxWidth: 600 }} />
                ) : (
                  <Space.Compact>
                    <CpInput name={`aws.region`} style={{ maxWidth: 600 }} />
                    <Tooltip title={`Browse`}>
                      <Button icon={<EllipsisOutlined />} onClick={() => setBrowserKey("region")} />
                    </Tooltip>
                  </Space.Compact>
                )}
                <CpErrorWarning name={`aws.region`} />
              </CpFormItem>
            ) : (
              <div className="mb-8">
                <CpFormItem>
                  <CpLabel name="skip">Region</CpLabel>
                  <FormItemValue>{region}</FormItemValue>
                </CpFormItem>
              </div>
            )}
            <div className="mb-4">
              <CpFormItem>
                <CpLabel name="skip">Image</CpLabel>
                <CpRadioGroup
                  name={`aws.image.type`}
                  className="mb-1"
                  style={{ width: 600 }}
                  options={[
                    { label: "Recommended", value: "recommended" },
                    { label: "Exact", value: "exact" },
                  ]}
                />
                {imageType === "exact" ? (
                  <>
                    <CpInput name={`aws.image.exact`} style={{ width: 600 }} />
                    <CpErrorWarning name={`aws.image.exact`} />
                  </>
                ) : imageType === "recommended" ? (
                  <>
                    <CpSelect name={`aws.image.recommended`} style={{ width: 600 }} options={recommendedAmis} />
                    <CpErrorWarning name={`aws.image.recommended`} />
                  </>
                ) : null}
              </CpFormItem>
            </div>
            {isCreate ? (
              <CpFormItem>
                <CpLabel name={`aws.vpcId`}>VPC ID</CpLabel>
                <Space.Compact>
                  <CpInput name={`aws.vpcId`} style={{ width: 600 }} />
                  <Tooltip title={"Browse"}>
                    <Button icon={<MoreHorizontal />} onClick={() => setBrowserKey("vpcId")} />
                  </Tooltip>
                </Space.Compact>
                <CpErrorWarning name={`aws.vpcId`} />
              </CpFormItem>
            ) : (
              <div className="mb-8">
                <CpFormItem>
                  <CpLabel name="skip">VPC ID</CpLabel>
                  <FormItemValue>{vpcId}</FormItemValue>
                </CpFormItem>
              </div>
            )}
            <CpFormItem>
              <CpLabel name={`aws.keyPair`}>Keypair</CpLabel>
              <Space.Compact>
                <CpInput name={`aws.keyPair`} style={{ width: 600 }} />
                <Tooltip title={"Browse"}>
                  <Button icon={<MoreHorizontal />} onClick={() => setBrowserKey("keyPair")} />
                </Tooltip>
              </Space.Compact>
              <CpErrorWarning name={`aws.keyPair`} />
            </CpFormItem>
            <CpFormItem>
              <div className="flex items-center gap-1">
                <div>Disk Encryption Key ARN</div>
                <AwsSsmIcon />
              </div>
              <FormItemDescription>
                This requires Autoscaler to use the{" "}
                <ExternalLink
                  label="KMS Key"
                  url={`https://docs.aws.amazon.com/autoscaling/ec2/userguide/key-policy-requirements-EBS-encryption.html`}
                  disableCopy
                />
              </FormItemDescription>
              <Space.Compact>
                <CpInput name={`aws.diskEncryptionKeyArn`} style={{ width: 600 }} />
                <Tooltip title={"Browse"}>
                  <Button icon={<MoreHorizontal />} onClick={() => setBrowserKey("diskEncryptionKeyArn")} />
                </Tooltip>
              </Space.Compact>
              <CpErrorWarning name={`aws.diskEncryptionKeyArn`} />
            </CpFormItem>
            <div style={{ maxWidth: 600 }}>
              <CpArrayString label="Security Group IDs" name={`aws.securityGroupIds`} />
            </div>
            <div className="mt-4">
              <CardTitle style={{ marginBottom: 0, fontSize: 20 }}>
                <div className={`flex items-baseline ${isAdvancedCollapsed ? "" : "mb-4"}`}>
                  <div
                    className={`flex items-center gap-2 color-link-hover cursor-pointer`}
                    onClick={() => {
                      setIsAdvancedCollapsed((x) => !x);
                    }}
                  >
                    <CaretRightOutlined rotate={isAdvancedCollapsed ? 0 : 90} className={`self-center`} />
                    <span>Advanced</span>
                    <CpDirtyIndicatorArrayIcon names={["provider-aws-advanced"]} />
                  </div>
                </div>
              </CardTitle>
            </div>
            <CardCollapsibleContent collapsed={isAdvancedCollapsed}>
              <div style={{ maxWidth: 600 }}>
                <CpArrayObject
                  factoryFn={() => ({ id: uuidv4(), name: "", value: "" })}
                  label={`AWS Tags`}
                  name={`aws.awsTags`}
                >
                  <CpArrayItemNameValuePair />
                </CpArrayObject>
              </div>
              <div style={{ maxWidth: 600 }}>
                <CpArrayObject
                  factoryFn={() => ({ id: uuidv4(), roleArn: "", externalId: "", sessionNamePrefix: "" })}
                  label={`Deploy Role Chain`}
                  labels={["Role ARN", "External ID", "Session Name Prefix"]}
                  name={`aws.deployRoleChain`}
                >
                  <CpArrayItemDeployRoleChain />
                </CpArrayObject>
              </div>
              <div style={{ maxWidth: 600 }}>
                <CpFormItem>
                  <div className="flex items-center gap-4">
                    <CpLabel name={`skip`}>Extra Node Policies</CpLabel>
                    <Button
                      className="ml-1"
                      size={"small"}
                      type={"default"}
                      onClick={() => setBrowserKey("iam_policies")}
                    >
                      Browse
                    </Button>
                  </div>
                  <div className="-mt-2">
                    <CpArrayString name={`aws.extraNodePolicies`} />
                  </div>
                </CpFormItem>
              </div>
              <CpFormItem>
                <div className="flex items-center gap-2">
                  <CpLabel name={`aws.skipCreateRoles`}>Skip Create Roles</CpLabel>
                  <CpSwitch name={`aws.skipCreateRoles`} />
                </div>
                <CpErrorWarning name={`aws.skipCreateRoles`} />
              </CpFormItem>
              <ProviderPreInstallScript provider="aws" />
              <ProviderNetworking provider="aws" />
              <ProviderAutoscaler provider="aws" />
            </CardCollapsibleContent>
          </CardContent>
        </Card>
      </div>
      {browserKey === "vpcId" ? (
        <NGProviderBrowser
          title="Browse AWS VPC IDs"
          initialValue={vpcId}
          fetchData={async () => {
            const form = store.getState().form.instances[formId];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "ec2-vpcs",
                cluster: inputToMk8s(values),
              },
            });
            return data.Vpcs;
          }}
          onOk={(value) => {
            formSetValue(formId, `aws.vpcId`, value as string);
            formTriggerValidation(formId);
          }}
          onClose={() => setBrowserKey("")}
          labels={["Name", "VPC ID", "CIDR Block", "Is Default"]}
          getData={(item: any) => {
            let name = "-";
            const nameTag = (item.Tags || []).find((t: any) => t.Key === "Name");
            if (nameTag) {
              name = nameTag.Value;
            }

            return [item.VpcId, name, item.VpcId, item.CidrBlock, String(item.IsDefault)];
          }}
        />
      ) : null}
      {browserKey === "keyPair" ? (
        <NGProviderBrowser
          title="Browse AWS EC2 Key Pairs"
          initialValue={keyPair}
          fetchData={async () => {
            const form = store.getState().form.instances[formId];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "ec2-keyPairs",
                cluster: inputToMk8s(values),
              },
            });
            return data.KeyPairs;
          }}
          onOk={(value) => {
            formSetValue(formId, `aws.keyPair`, value as string);
            formTriggerValidation(formId);
          }}
          onClose={() => setBrowserKey("")}
          labels={["Name", "Type", "Create Time"]}
          getData={(item: any) => [
            item.KeyName,
            item.KeyName,
            item.KeyType,
            dateString(item.CreateTime, { format: formatStrings.log }),
          ]}
        />
      ) : null}
      {browserKey === "diskEncryptionKeyArn" ? (
        <NGProviderBrowser
          title="Browse AWS KMS Keys"
          initialValue={diskEncryptionKeyArn}
          fetchData={async () => {
            const form = store.getState().form.instances[formId];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "kms-keys",
                cluster: inputToMk8s(values),
              },
            });
            return data.Keys;
          }}
          onOk={(value) => {
            formSetValue(formId, `aws.diskEncryptionKeyArn`, value as string);
            formTriggerValidation(formId);
          }}
          onClose={() => setBrowserKey("")}
          labels={["Arn"]}
          getData={(item: any) => [item.KeyArn, item.KeyArn]}
        />
      ) : null}
      {browserKey === "securityGroupId" ? (
        <NGProviderBrowser
          title="Browse AWS Security Groups"
          initialValue={securityGroupIds.map((i) => i.value)}
          fetchData={async () => {
            const form = store.getState().form.instances[formId];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "ec2-securityGroups",
                cluster: inputToMk8s(values),
              },
            });
            return data.SecurityGroups;
          }}
          multipleChoice
          onOk={(_value) => {
            const values: string[] = _value as any;
            for (const value of values) {
              if (securityGroupIds.some((i) => i.value === value)) {
                continue;
              }
              securityGroupIds.push({ id: uuidv4(), value: value });
              formSetValue(formId, `aws.securityGroupIds`, securityGroupIds);
            }
            formTriggerValidation(formId);
          }}
          onClose={() => setBrowserKey("")}
          labels={["Name", "Description", "Security Group ID"]}
          getData={(item: any) => [item.GroupId, item.GroupName, item.Description, item.GroupId]}
        />
      ) : null}
      {browserKey === "iam_policies" ? (
        <NGProviderBrowser
          title="Browse AWS Policies"
          initialValue={extraNodePolicies.map((i) => i.value)}
          fetchData={async () => {
            const form = store.getState().form.instances[formId];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "iam_policies",
                cluster: inputToMk8s(values),
              },
            });
            return data.Policies;
          }}
          multipleChoice
          onOk={(_value) => {
            const values: string[] = _value as any;
            for (const value of values) {
              if (extraNodePolicies.some((i) => i.value === value)) {
                continue;
              }
              extraNodePolicies.push({ id: uuidv4(), value: value });
              formSetValue(formId, `aws.extraNodePolicies`, extraNodePolicies);
            }
            formTriggerValidation(formId);
          }}
          onClose={() => setBrowserKey("")}
          sortable
          filteredColumns={["Name"]}
          labels={["Name", "Arn"]}
          columnRenderers={[
            {
              label: "Name",
              renderer: (_, __, itemData) => {
                if (itemData[2].includes("iam::aws:policy")) {
                  return <AwsPolicyIcon className="mr-1" />;
                }
                return null as any;
              },
            },
          ]}
          getData={(item: any) => [item.Arn, item.PolicyName, item.Arn]}
        />
      ) : null}
    </>
  );
};
